'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '@/config/theme';
import { ButtonProps } from '@/types/components';

const getButtonVariantStyles = (variant: string) => {
  switch (variant) {
    case 'primary':
      return css`
        background: ${theme.colors.gradients.primary};
        color: ${theme.colors.white};
        border: none;

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: ${theme.shadows.accentHover};
        }
      `;
    case 'secondary':
      return css`
        background-color: ${theme.colors.button.secondary};
        color: ${theme.colors.white};
        border: 2px solid ${theme.colors.button.secondary};

        &:hover:not(:disabled) {
          background-color: ${theme.colors.button.secondaryHover};
          opacity: 0.9;
        }
      `;
    case 'outline':
      return css`
        background-color: transparent;
        border: 1px solid ${theme.colors.border.primary};

        &:hover:not(:disabled) {
          background-color: ${theme.colors.light.background.hover};
          border-color: ${theme.colors.gray[400]};
        }
      `;
    case 'ghost':
      return css`
        background-color: transparent;
        color: ${theme.colors.primary.accent};
        border: 2px solid transparent;

        &:hover:not(:disabled) {
          background-color: ${theme.colors.overlay.accent};
        }
      `;
    case 'text':
      return css`
        background-color: transparent;
        color: ${theme.colors.primary.accent};
        border: 2px solid transparent;
        padding: 4px 8px;

        &:hover:not(:disabled) {
          text-decoration: underline;
        }
      `;
    case 'white':
      return css`
        background-color: ${theme.colors.white};
        color: ${theme.colors.black};
        border: 1px solid ${theme.colors.border.primary};
        box-shadow: ${theme.shadows.sm};

        &:hover:not(:disabled) {
          background-color: ${theme.colors.gray[50]};
          border-color: ${theme.colors.border.tertiary};
          box-shadow: ${theme.shadows.md};
        }
      `;
    case 'danger':
      return css`
        background-color: ${theme.colors.button.danger};
        color: ${theme.colors.white};
        border: none;

        &:hover:not(:disabled) {
          background-color: ${theme.colors.button.dangerHover};
        }
      `;

    case 'info':
      return css`
        background-color: ${theme.colors.button.info};
        color: ${theme.colors.white};
        border: none;

        &:hover:not(:disabled) {
          background-color: ${theme.colors.button.infoHover};
        }
      `;
    default:
      return css``;
  }
};

const getButtonSizeStyles = (size: string) => {
  switch (size) {
    case 'small':
      return css`
        padding: 4px 16px;
        font-size: 14px;
        border-radius: 4px;
      `;
    case 'large':
      return css`
        padding: 16px 32px;
        font-size: 14px;
        font-weight: 700;
      `;
    case 'medium':
    default:
      return css`
        padding: 6px 16px;
        font-size: 14px;
        border-radius: 4px;
        svg{
          width: 20px;
          height: 20px;
        }
      `;
  }
};

const StyledButton = styled.button<{
  $variant: string;
  $size: string;
  $fullWidth: boolean;
  $width?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  $loading: boolean;
  $backgroundColor?: string;
  $textColor?: string;
  $hoverBackgroundColor?: string;
  $hoverTextColor?: string;
  $borderColor?: string;
  $hoverBorderColor?: string;
  $height?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.gray.darkText};
  font-weight: 500;
  font-family: 'Open Sans', sans-serif;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  width: ${({ $fullWidth, $width }) => {
    if ($fullWidth) return '100%';
    if ($width === 'xs') return '80px';
    if ($width === 'sm') return '120px';
    if ($width === 'md') return '160px';
    if ($width === 'lg') return '200px';
    if ($width === 'xl') return '240px';
    return 'auto';
  }};
  opacity: ${({ $loading }) => ($loading ? 0.7 : 1)};
  ${({ $height }) => $height && css`
    height: ${$height} !important;
  `}
  
  ${({ $variant }) => getButtonVariantStyles($variant)}
  ${({ $size }) => getButtonSizeStyles($size)}
  
  ${({ $backgroundColor }) => $backgroundColor && css`
    background-color: ${$backgroundColor} !important;
  `}
  
  ${({ $textColor }) => $textColor && css`
    color: ${$textColor} !important;
  `}
  
  ${({ $borderColor }) => {
    if ($borderColor === '') {
      return css`
        border: none !important;
      `;
    }
    if ($borderColor) {
      return css`
        border-color: ${$borderColor} !important;
      `;
    }
    return css``;
  }}

  &:hover:not(:disabled) {
    ${({ $hoverBackgroundColor }) => $hoverBackgroundColor && css`
      background-color: ${$hoverBackgroundColor} !important;
    `}
    
    ${({ $hoverTextColor }) => $hoverTextColor && css`
      color: ${$hoverTextColor} !important;
    `}
    
    ${({ $hoverBorderColor }) => $hoverBorderColor && css`
      border-color: ${$hoverBorderColor} !important;
    `}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary.accent};
    outline-offset: 2px;
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'medium',
  color = 'primary',
  disabled = false,
  fullWidth = false,
  width,
  loading = false,
  type = 'button',
  startIcon,
  endIcon,
  className,
  backgroundColor,
  textColor,
  hoverBackgroundColor,
  hoverTextColor,
  borderColor,
  hoverBorderColor,
  height,
  style,
  ...rest
}) => {
  return (
    <StyledButton
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      $width={width}
      $loading={loading}
      $backgroundColor={backgroundColor}
      $textColor={textColor}
      $hoverBackgroundColor={hoverBackgroundColor}
      $hoverTextColor={hoverTextColor}
      $borderColor={borderColor}
      $hoverBorderColor={hoverBorderColor}
      $height={height}
      className={className}
      style={style}
      {...rest}
    >
      {loading && <Spinner />}
      {!loading && startIcon && startIcon}
      {children}
      {!loading && endIcon && endIcon}
    </StyledButton>
  );
};

export default Button;

